import { FormGroup, FormControl, FormBuilder, NonNullableFormBuilder } from '@angular/forms';

import { Spreadsheet, Worksheet } from './data-mappers';
import { Tab } from './upload-sheet-dialog.component';
import { relevantInputValidator } from './relevant-input.validator';
import { validNameValidator } from '@mode/dataset/ui';

export interface UploadSheetFormData {
  tab: Tab;
  searchText: string;
  spreadsheetId: string;
  worksheetId: string | number;
  url: string;
  name: string;
  description: string;
}

type Form<TData> = Required<{
  [K in keyof TData]: FormControl<TData[K]>;
}>;

export class UploadSheetForm extends FormGroup<Form<UploadSheetFormData>> {
  readonly tab = this.controls.tab;
  readonly searchText = this.controls.searchText;
  readonly url = this.controls.url;
  readonly spreadsheetId = this.controls.spreadsheetId;
  readonly worksheetId = this.controls.worksheetId;
  readonly name = this.controls.name;
  readonly description = this.controls.description;

  get activeTab(): Tab {
    return this.tab.value;
  }

  get urlError(): boolean {
    return this.url.value.length && this.url.touched && this.getError('invalidUrl');
  }

  get nameError(): boolean {
    return this.name.touched && this.name.getError('invalidName');
  }

  constructor(readonly fb: NonNullableFormBuilder = new FormBuilder().nonNullable) {
    super(
      fb.group<Form<UploadSheetFormData>>({
        tab: fb.control('Search'),
        searchText: fb.control(''),
        spreadsheetId: fb.control(''),
        worksheetId: fb.control(''),
        url: fb.control(''),
        name: fb.control('', { validators: [validNameValidator] }),
        description: fb.control(''),
      }).controls,
      { validators: [relevantInputValidator] }
    );
  }

  search(searchText: string) {
    this.patchValue({ searchText });
  }

  selectTab(tab: Tab) {
    this.reset();
    this.patchValue({ tab });
  }

  selectSpreadsheet({ id }: Readonly<Spreadsheet>) {
    this.patchValue({ spreadsheetId: id });
  }

  selectWorksheet(worksheet: Readonly<Worksheet> | null) {
    this.patchValue({ worksheetId: worksheet?.id ?? '' });
  }
}
